import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../service/Api";
import Drawer from "../../components/common/Drawer";
import {
    Loader2,
    Package,
    Users,
    Plus,
    FileText,
    ArrowLeft,
    Sparkles,
    Layers,
} from "lucide-react";
import toast from "react-hot-toast";
import FeaturePreviewWrapper from "../../components/collateral/FeaturePreviews/FeaturePreviewWrapper";

const PAGE_SIZE = 6;

const initialProspectForm = {
    name: "",
    domain: "",
};

export default function HtmlCollateralCreate() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [features, setFeatures] = useState([]);
    const [selectedFeatureIds, setSelectedFeatureIds] = useState([]);
    const [hoveredFeature, setHoveredFeature] = useState(null);
    const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });

    const [prospects, setProspects] = useState([]);
    const [prospectsMeta, setProspectsMeta] = useState({
        page: 1,
        pageSize: PAGE_SIZE,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    });
    const [prospectsLoading, setProspectsLoading] = useState(false);
    const [selectedProspectId, setSelectedProspectId] = useState(null);
    const [selectedProspect, setSelectedProspect] = useState(null);
    const selectedProspectIdRef = useRef(null);

    const [description, setDescription] = useState("");
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [prospectForm, setProspectForm] = useState(initialProspectForm);
    const [creatingProspect, setCreatingProspect] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [rephrasing, setRephrasing] = useState(false);
    const [pageError, setPageError] = useState(null);

    const getDefaultPreviewData = useCallback((featureTitle) => {
        const defaults = {
            'Scroll-Triggered Number Counters': {
                targetValue: 2500,
                prefix: '$',
                suffix: 'M+',
                label: 'Revenue Generated',
                duration: 2000,
            },
            'Image Reveal on Scroll (Curtain Effect': {
                direction: 'left',
                overlayColor: 'bg-purple-600',
                title: 'Our Amazing Product',
                subtitle: 'Revealed with stunning animation',
            },
            'Animated Gradient Background': {
                colors: ['#667eea', '#764ba2', '#f093fb', '#4facfe'],
                title: 'Welcome to the Future',
                subtitle: 'Experience innovation like never before',
            },
            'Split-Screen Reveal': {
                problemText: 'Manual, time-consuming processes',
                solutionText: 'AI-powered automation',
                centerTitle: 'Your Perfect Solution',
                centerText: 'Transforms your entire workflow',
            },
            'Rotating Hero Text with Fixed Prefix': {
                prefix: 'We help you',
                phrases: [
                    'boost productivity',
                    'save valuable time',
                    'increase revenue',
                    'scale faster',
                    'automate workflows',
                ],
                subtitle: 'Transform your business today',
            },
            'Infinite Logo Crawler': {
                logos: ['🏢', '🚀', '💼', '🎯', '⚡', '🌟', '💡', '🔥', '🎨', '🛠️', '📱', '💻'],
                speed: 25,
                direction: 'left',
                title: 'Trusted by Industry Leaders',
            },
        };
        return defaults[featureTitle] || {};
    }, []);

    const handleFeatureMouseEnter = useCallback((feature, event) => {
        const rect = event.currentTarget.getBoundingClientRect();

        const shouldShowLeft = window.innerWidth - rect.right < 520;

        setPreviewPosition({
            x: shouldShowLeft ? rect.left - 520 : rect.right + 20,
            y: Math.max(20, rect.top - 50),
        });

        setHoveredFeature(feature);
    }, []);

    const handleFeatureMouseLeave = useCallback(() => {
        setHoveredFeature(null);
    }, []);

    const parseProspectsResponse = useCallback(
        (
            response,
            fallbackPage = 1,
            preferredProspectId = null,
            currentSelectedId = null
        ) => {
            if (!response?.data?.success) {
                setProspects([]);
                setProspectsMeta((prev) => ({
                    ...prev,
                    total: 0,
                    page: fallbackPage,
                    totalPages: 1,
                    hasNextPage: false,
                    hasPreviousPage: false,
                }));
                return;
            }

            const payload = response.data.data || {};
            const items = Array.isArray(payload) ? payload : payload.items || [];
            const total =
                typeof payload.total === "number" ? payload.total : items.length;

            setProspects(items);
            setProspectsMeta({
                page: payload.page || fallbackPage,
                pageSize: payload.pageSize || PAGE_SIZE,
                total,
                totalPages: payload.totalPages || Math.max(1, Math.ceil(total / PAGE_SIZE)),
                hasNextPage: Boolean(payload.hasNextPage),
                hasPreviousPage: Boolean(payload.hasPreviousPage),
            });

            if (items.length === 0) {
                setSelectedProspect(null);
                setSelectedProspectId(null);
                selectedProspectIdRef.current = null;
                return;
            }

            const targetId = preferredProspectId ?? currentSelectedId;
            const existingSelection =
                targetId && items.find((item) => item.id === targetId);
            const nextSelection = existingSelection || items[0];
            const nextId = nextSelection?.id ?? null;

            setSelectedProspectId(nextId);
            setSelectedProspect(nextSelection ?? null);
            selectedProspectIdRef.current = nextId;
        },
        []
    );

    const fetchProspects = useCallback(
        async (page = 1, preferredProspectId = null) => {
            setProspectsLoading(true);
            try {
                const response = await apiService.getTailspinProspects({
                    page,
                    pageSize: PAGE_SIZE,
                });
                parseProspectsResponse(
                    response,
                    page,
                    preferredProspectId,
                    selectedProspectIdRef.current
                );
            } catch (error) {
                console.error("Failed to fetch prospects:", error);
                toast.error("Failed to load prospects");
            } finally {
                setProspectsLoading(false);
            }
        },
        [parseProspectsResponse]
    );

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            setPageError(null);
            try {
                const [productsResponse, featuresResponse] = await Promise.all([
                    apiService.getTailspinProducts(),
                    apiService.getCollateralFeatures()
                ]);

                if (productsResponse.data.success) {
                    const items = productsResponse.data.data || [];
                    setProducts(items);
                } else {
                    setProducts([]);
                }

                if (featuresResponse.data?.success) {
                    setFeatures(featuresResponse.data.data || []);
                }

                await fetchProspects(1);
            } catch (error) {
                console.error("Failed to initialize collateral creation page:", error);
                setPageError(
                    error.response?.data?.message ||
                    "Unable to load Tailspin data. Please try again."
                );
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, [fetchProspects]);

    const handleCreateProspect = async (event) => {
        event.preventDefault();
        if (creatingProspect) {
            return;
        }

        const name = prospectForm.name.trim();
        const domain = prospectForm.domain.trim();

        if (!name || !domain) {
            toast.error("Prospect name and domain are required");
            return;
        }

        setCreatingProspect(true);
        try {
            const response = await apiService.createTailspinProspect({
                name,
                domain,
            });

            if (response.data.success) {
                const createdProspect = response.data.data;
                toast.success("Prospect created");
                setProspectForm(initialProspectForm);
                setIsDrawerOpen(false);
                await fetchProspects(1, createdProspect.id);
            } else {
                toast.error(response.data.message || "Failed to create prospect");
            }
        } catch (error) {
            console.error("Failed to create prospect:", error);
            toast.error(error.response?.data?.error || "Failed to create prospect");
        } finally {
            setCreatingProspect(false);
        }
    };

    const handleProspectSelection = (prospect) => {
        setSelectedProspectId(prospect.id);
        selectedProspectIdRef.current = prospect.id;
        setSelectedProspect(prospect);
    };

    const handleProspectPageChange = async (direction) => {
        const { page, totalPages } = prospectsMeta;
        if (direction === "previous" && page > 1) {
            await fetchProspects(page - 1);
        }
        if (direction === "next" && page < totalPages) {
            await fetchProspects(page + 1);
        }
    };

    const handleRephrase = async () => {
        if (!description.trim()) {
            toast.error("Please enter a description first");
            return;
        }

        setRephrasing(true);
        try {
            const response = await apiService.rephrasePrompt(description.trim());

            if (response.data.success && response.data.data?.rephrased_prompt) {
                setDescription(response.data.data.rephrased_prompt);
                toast.success("Description rephrased successfully");
            } else {
                toast.error(response.data.message || "Failed to rephrase description");
            }
        } catch (error) {
            console.error("Failed to rephrase description:", error);
            toast.error(error.response?.data?.message || "Failed to rephrase description");
        } finally {
            setRephrasing(false);
        }
    };

    const handleGenerateCollateral = async () => {
        setPageError(null);

        if (!selectedProductIds || selectedProductIds.length === 0) {
            setPageError("Select at least one product to continue.");
            return;
        }

        if (!selectedProspectId) {
            setPageError("Select or create a prospect to continue.");
            return;
        }

        if (!description.trim()) {
            setPageError("Provide a short description for the collateral.");
            return;
        }

        setGenerating(true);
        try {
            // Call the /generate-code route instead of regular collateral creation
            const response = await apiService.generateCodeCollateral({
                product_id: selectedProductIds,
                prospect_id: selectedProspectId,
                description: description.trim(),
                features: selectedFeatureIds,
            });

            if (response.data.success) {
                const created = response.data.data;
                toast.success("HTML Collateral generated");

                // Redirect to HtmlCollateral page with the slug
                if (created?.slug) {
                    navigate(`/tailspin/html-collateral/${created.slug}`);
                } else {
                    navigate("/tailspin/dashboard");
                }
            } else {
                setPageError(response.data.message || "Failed to generate HTML collateral.");
            }
        } catch (error) {
            console.error("Failed to generate HTML collateral:", error);
            setPageError(
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Failed to generate HTML collateral."
            );
        } finally {
            setGenerating(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: '#F8F7F4' }}>
                <div className="text-center">
                    <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin" style={{ color: '#FF6B5A' }} />
                    <p style={{ color: '#0A0A10' }}>Loading HTML collateral creator...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-10" style={{ backgroundColor: '#F8F7F4' }}>
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <button
                            type="button"
                            onClick={() => navigate("/tailspin/dashboard")}
                            className="mb-4 inline-flex items-center text-sm font-medium transition"
                            style={{ color: '#FF6B5A' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#e55a4a'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#FF6B5A'; }}
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" style={{ color: 'inherit' }} />
                            Back to dashboard
                        </button>
                        <h1 className="text-3xl font-bold" style={{ color: '#0A0A10' }}>
                            Create HTML Collateral
                        </h1>
                        <p className="mt-2" style={{ color: '#0A0A10', opacity: 0.7 }}>
                            Choose a product, select or create a prospect, and describe the HTML collateral you need.
                        </p>
                    </div>
                </div>

                {pageError && (
                    <div className="mb-6 rounded-lg border p-4" style={{ borderColor: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)', color: '#FF6B5A' }}>
                        {pageError}
                    </div>
                )}

                {/* Product Selection */}
                <section className="mb-8 rounded-xl border p-6 shadow-sm" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                    <div className="mb-4 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)' }}>
                            <Package className="h-5 w-5" style={{ color: '#FF6B5A' }} />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                                Select Products
                            </h2>
                            <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                                Choose one or more products this collateral will focus on.
                            </p>
                        </div>
                    </div>

                    {products.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-6 text-center" style={{ borderColor: '#D7B472', color: '#0A0A10', opacity: 0.7 }}>
                            No products found. Add a product in onboarding to continue.
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {products.map((product) => {
                                    const isSelected = selectedProductIds.includes(product.id);
                                    return (
                                        <button
                                            key={product.id}
                                            type="button"
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedProductIds(prev => prev.filter(id => id !== product.id));
                                                } else {
                                                    setSelectedProductIds(prev => [...prev, product.id]);
                                                }
                                            }}
                                            className="flex w-full flex-col rounded-lg border p-4 text-left transition"
                                            style={isSelected ? { borderColor: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)' } : { borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}
                                            onMouseEnter={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.05)'; } }}
                                            onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.backgroundColor = '#F8F7F4'; } }}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start gap-3 flex-1">
                                                    <div className="mt-1 flex-shrink-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => { }}
                                                            className="h-4 w-4 rounded border-gray-300"
                                                            style={{ accentColor: '#FF6B5A' }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>
                                                            {product.name}
                                                        </h3>
                                                        {product.domain && (
                                                            <p className="text-sm" style={{ color: '#FF6B5A' }}>{product.domain}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <span className="rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: '#FF6B5A', opacity: 0.2, color: '#FF6B5A' }}>
                                                        Selected
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {selectedProductIds.length > 0 && (
                                <div className="mt-4 text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                                    {selectedProductIds.length} product{selectedProductIds.length !== 1 ? 's' : ''} selected
                                </div>
                            )}
                        </>
                    )}
                </section>

                {/* Features Selection - Same as before */}
                <section className="mb-8 rounded-xl border p-6 shadow-sm" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                    <div className="mb-4 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)' }}>
                            <Layers className="h-5 w-5" style={{ color: '#FF6B5A' }} />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                                Select Features (Optional)
                            </h2>
                            <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                                Choose features to include in the HTML collateral.
                            </p>
                        </div>
                    </div>

                    {features.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-6 text-center" style={{ borderColor: '#D7B472', color: '#0A0A10', opacity: 0.7 }}>
                            No features available.
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {features.map((feature) => {
                                    const isSelected = selectedFeatureIds.includes(feature.id);
                                    return (
                                        <button
                                            key={feature.id}
                                            type="button"
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedFeatureIds(prev => prev.filter(id => id !== feature.id));
                                                } else {
                                                    setSelectedFeatureIds(prev => [...prev, feature.id]);
                                                }
                                            }}
                                            className="flex w-full flex-col rounded-lg border p-4 text-left transition"
                                            style={isSelected ? { borderColor: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)' } : { borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}
                                            onMouseEnter={(e) => {
                                                handleFeatureMouseEnter(feature, e);
                                                if (!isSelected) {
                                                    e.currentTarget.style.borderColor = '#FF6B5A';
                                                    e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.05)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                handleFeatureMouseLeave();
                                                if (!isSelected) {
                                                    e.currentTarget.style.borderColor = '#D7B472';
                                                    e.currentTarget.style.backgroundColor = '#F8F7F4';
                                                }
                                            }}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start gap-3 flex-1">
                                                    <div className="mt-1 flex-shrink-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => { }}
                                                            className="h-4 w-4 rounded border-gray-300"
                                                            style={{ accentColor: '#FF6B5A' }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>
                                                            {feature.value.title}
                                                        </h3>
                                                        <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>{feature.value.description}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {selectedFeatureIds.length > 0 && (
                                <div className="mt-4 text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                                    {selectedFeatureIds.length} feature{selectedFeatureIds.length !== 1 ? 's' : ''} selected
                                </div>
                            )}
                        </>
                    )}
                </section>

                {/* Prospect Selection - Keeping the same structure */}
                <section className="mb-8 rounded-xl border p-6 shadow-sm" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                    <div className="mb-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(215, 180, 114, 0.15)' }}>
                                <Users className="h-5 w-5" style={{ color: '#D7B472' }} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                                    Select Prospect
                                </h2>
                                <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                                    Choose an existing prospect or create a new one.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsDrawerOpen(true)}
                            className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition"
                            style={{ borderColor: '#D7B472', color: '#D7B472' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.1)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            <Plus className="h-4 w-4" />
                            New Prospect
                        </button>
                    </div>

                    {prospectsLoading ? (
                        <div className="flex items-center justify-center py-8" style={{ color: '#0A0A10', opacity: 0.7 }}>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" style={{ color: '#D7B472' }} />
                            Loading prospects...
                        </div>
                    ) : prospects.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-6 text-center" style={{ borderColor: '#D7B472', color: '#0A0A10', opacity: 0.7 }}>
                            No prospects yet. Create one to continue.
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {prospects.map((prospect) => {
                                    const isSelected = selectedProspectId === prospect.id;
                                    return (
                                        <button
                                            key={prospect.id}
                                            type="button"
                                            onClick={() => handleProspectSelection(prospect)}
                                            className="flex w-full flex-col rounded-lg border p-4 text-left transition"
                                            style={isSelected ? { borderColor: '#D7B472', backgroundColor: 'rgba(215, 180, 114, 0.1)' } : { borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}
                                            onMouseEnter={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.05)'; } }}
                                            onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.backgroundColor = '#F8F7F4'; } }}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <h3 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>
                                                        {prospect.name}
                                                    </h3>
                                                    {prospect.domain && (
                                                        <p className="text-sm" style={{ color: '#D7B472' }}>
                                                            {prospect.domain}
                                                        </p>
                                                    )}
                                                </div>
                                                {isSelected && (
                                                    <span className="rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: '#D7B472', opacity: 0.2, color: '#D7B472' }}>
                                                        Selected
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-6 flex items-center justify-between text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                                <p>
                                    Page {prospectsMeta.page} of {prospectsMeta.totalPages} •{" "}
                                    {prospectsMeta.total} prospects
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleProspectPageChange("previous")}
                                        disabled={!prospectsMeta.hasPreviousPage}
                                        className="rounded-lg border px-3 py-2 font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
                                        style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                                        onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = '#FF6B5A'; }}
                                        onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = '#D7B472'; }}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleProspectPageChange("next")}
                                        disabled={!prospectsMeta.hasNextPage}
                                        className="rounded-lg border px-3 py-2 font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
                                        style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                                        onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = '#FF6B5A'; }}
                                        onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = '#D7B472'; }}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </section>

                {/* Collateral Description */}
                <section className="mb-10 rounded-xl border p-6 shadow-sm" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                    <div className="mb-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)' }}>
                                <FileText className="h-5 w-5" style={{ color: '#FF6B5A' }} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                                    Describe the HTML Collateral
                                </h2>
                                <p className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                                    Provide details about the HTML collateral you want to generate.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleRephrase}
                            disabled={rephrasing || generating || !description.trim()}
                            className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ borderColor: '#D7B472', color: '#D7B472' }}
                            onMouseEnter={(e) => { if (!rephrasing && description.trim()) { e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.1)'; } }}
                            onMouseLeave={(e) => { if (!rephrasing && description.trim()) { e.currentTarget.style.backgroundColor = 'transparent'; } }}
                        >
                            {rephrasing ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Rephrasing...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="h-4 w-4" />
                                    Rephrase
                                </>
                            )}
                        </button>
                    </div>
                    <textarea
                        rows={6}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        placeholder="Example: A futuristic landing page hero section with a dark gradient background, glowing animated text, a 3D-style floating product card, and a 'Get Started' button that pulses on hover."
                        className="w-full rounded-lg border px-4 py-3 text-sm shadow-sm transition focus:outline-none focus:ring-2"
                        style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.ringColor = '#FF6B5A'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                    />
                </section>

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleGenerateCollateral}
                        disabled={generating}
                        className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-white shadow-sm transition disabled:cursor-not-allowed"
                        style={{ backgroundColor: generating ? '#D7B472' : '#FF6B5A' }}
                        onMouseEnter={(e) => { if (!generating) e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                        onMouseLeave={(e) => { if (!generating) e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
                    >
                        {generating ? (
                            <>
                                <Loader2 className="h-5 w-5 animate-spin" />
                                Generating HTML...
                            </>
                        ) : (
                            <>
                                Generate HTML Collateral
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Feature Preview */}
            {hoveredFeature && (
                <FeaturePreviewWrapper
                    featureTitle={hoveredFeature.value?.title}
                    data={getDefaultPreviewData(hoveredFeature.value?.title)}
                    isVisible={true}
                    position={previewPosition}
                />
            )}

            <Drawer
                isOpen={isDrawerOpen}
                onClose={() => {
                    setIsDrawerOpen(false);
                    setProspectForm(initialProspectForm);
                }}
                title="Create Prospect"
                description="Add a new prospect for HTML collateral."
            >
                <form className="space-y-4" onSubmit={handleCreateProspect}>
                    <div>
                        <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                            Prospect Name
                        </label>
                        <input
                            type="text"
                            value={prospectForm.name}
                            onChange={(event) =>
                                setProspectForm((prev) => ({ ...prev, name: event.target.value }))
                            }
                            placeholder="Acme Logistics"
                            className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                            style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                            required
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                            Prospect Domain
                        </label>
                        <input
                            type="text"
                            value={prospectForm.domain}
                            onChange={(event) =>
                                setProspectForm((prev) => ({ ...prev, domain: event.target.value }))
                            }
                            placeholder="example.com"
                            className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                            style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                            required
                        />
                    </div>
                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={creatingProspect}
                            className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-white transition disabled:cursor-not-allowed"
                            style={{ backgroundColor: creatingProspect ? '#D7B472' : '#D7B472' }}
                            onMouseEnter={(e) => { if (!creatingProspect) e.currentTarget.style.backgroundColor = '#c5a060'; }}
                            onMouseLeave={(e) => { if (!creatingProspect) e.currentTarget.style.backgroundColor = '#D7B472'; }}
                        >
                            {creatingProspect ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Plus className="h-4 w-4" />
                                    Save Prospect
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Drawer>
        </div>
    );
}
